import React, { useState, useMemo } from 'react';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import HelpIcon from 'src/components/help-icon';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import { useLoggedInUser } from 'src/services/api/auth';
import LabelChip, { PROPERTY_LABEL_CHIP_COLOR, labelColorToCss } from 'src/components/labels/LabelChip';
import { useNotifications } from 'src/notifications';
import { isStandardErrorResponse } from 'src/api/common';
import { type ILabel, LabelType } from 'src/services/api/labels';
import {
    useLeadLabels,
    useCreateLeadLabel,
    useUpdateLeadLabel,
    useDeleteLeadLabel,
    usePropertyLabels,
    useCreatePropertyLabel,
    useUpdatePropertyLabel,
    useDeletePropertyLabel,
} from 'src/services/api';
import { Transition } from 'src/components/ModalTransition';
import {
    BlackButtonWhiteText,
    ModalSuccessButton,
    RedButtonWhiteText
} from 'src/common/Buttons';
import Auth from 'src/services/api/auth/Auth';
import { labelColorOptions } from 'src/theme/colors';

import styles from 'src/components/edit-labels-modal/EditLabelsModal.module.css';
import modalButtonStyles from 'src/common/ModalButton.module.css';

interface IColorPickerProps {
    value: string | null;
    onChange(color: string | null): void;
}

function ColorPicker({ value, onChange }: IColorPickerProps) {
    return <div className={styles.colorPicker}>
        <Typography variant="caption" color="textSecondary" className={styles.colorPickerLabel}>Color</Typography>
        <div className={styles.colorRow}>
            {labelColorOptions.map((color) => (
                <button
                    key={color}
                    type="button"
                    aria-label={`Label color ${color}`}
                    className={`${styles.colorSwatch} ${value === color ? styles.colorSwatchSelected : ''}`}
                    style={{ backgroundColor: color }}
                    onClick={() => onChange(color)}
                />
            ))}
        </div>
    </div>;
}

interface ILabelRowProps {
    label: ILabel;
    onEdit(): void;
    onDelete(): void;
}

function LabelRow(props: ILabelRowProps) {
    return <div className={styles.propertyLabelRow} data-testid={`label-row-${props.label.name}`}>
        <LabelChip
            name={props.label.name}
            color={props.label.type === LabelType.PROPERTY ?
                labelColorToCss(props.label.color) || PROPERTY_LABEL_CHIP_COLOR :
                undefined}
            size="body"
        />
        <div className={styles.labelActions}>
            <IconButton onClick={props.onEdit} data-testid={`edit-label-button-${props.label.name}`}>
                <EditIcon />
            </IconButton>
            <IconButton onClick={props.onDelete} data-testid={`delete-label-button-${props.label.name}`}>
                <DeleteIcon />
            </IconButton>
        </div>
    </div>;
}

interface IAddLabelFormProps {
    labelName: string;
    labelType: string;
    labelColor: string | null;
    setLabelName(labelName: string): void;
    setLabelColor(labelColor: string | null): void;
    onSave(labelName: string): void;
}

function AddLabelForm({
    labelName,
    labelType,
    labelColor,
    setLabelName,
    setLabelColor,
    onSave,
}: IAddLabelFormProps) {
    const handleChangeName = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLabelName(e.target.value);
    };

    const handleSave = (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        onSave(labelName);
    };

    return <form onSubmit={handleSave}>
        <TextField
            autoFocus
            margin="dense"
            placeholder="New Label Name"
            name="label_name"
            value={labelName}
            type="text"
            // reflects backend limit
            inputProps={{ maxLength: 25 }}
            fullWidth
            variant="standard"
            onChange={handleChangeName}
            data-testid="new-label-name-field"
        />
        {labelType === 'property' && (
            <ColorPicker value={labelColor} onChange={setLabelColor} />
        )}
        <Button
            variant="contained"
            data-testid="new-label-add-button"
            disabled={labelName.trim() === ''}
            className={`${modalButtonStyles.modalButton} ${styles.addLabelButton}`}
            onClick={() => handleSave()}
        >Add</Button>
    </form>;
}

interface IEditLabelFormProps {
    label: ILabel;
    onSave: (labelId: number, labelName: string, labelColor: string | null) => Promise<void>;
    onClose: () => void;
}

function EditLabelForm({ label, onClose, onSave }: IEditLabelFormProps) {
    const [labelName, setLabelName] = useState(label.name);
    const [labelColor, setLabelColor] = useState<string | null>(labelColorToCss(label.color) || null);

    const handleSave = async (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        await onSave(label.id, labelName, labelColor);
        onClose();
    };

    return <form onSubmit={handleSave} className={styles.editForm}>
        <TextField
            id="label-name"
            label="Label Name"
            fullWidth
            variant="standard"
            margin="dense"
            value={labelName}
            onChange={(ev) => setLabelName(ev.target.value)}
            // reflects backend limit
            inputProps={{ maxLength: 25 }}
            data-testid="edit-label-name-field"
        />
        {label.type === LabelType.PROPERTY && (
            <ColorPicker value={labelColor} onChange={setLabelColor} />
        )}
        <div className={styles.modalButtonRow}>
            <BlackButtonWhiteText
                onClick={onClose}
            >
                Back
            </BlackButtonWhiteText>
            <ModalSuccessButton
                onClick={() => handleSave()}
                disabled={labelName.trim() === ''}
            >
                Save
            </ModalSuccessButton>
        </div>
    </form>;
}

interface IConfirmDeleteFormProps {
    label: ILabel;
    onCancel: () => void;
    onConfirm: () => Promise<void>;
}

function ConfirmDeleteForm({ label, onCancel, onConfirm }: IConfirmDeleteFormProps) {
    return <form className={styles.confirmDeleteForm}>
        <DialogContentText>
            Are you sure you want to delete label &quot;{label.name}&quot; for your entire team?
        </DialogContentText>
        <div className={`${styles.modalButtonRow} mt-2`}>
            <BlackButtonWhiteText onClick={onCancel} data-testid="delete-label-cancel-button">Back</BlackButtonWhiteText>
            <RedButtonWhiteText onClick={onConfirm} data-testid="delete-label-confirm-button">Delete</RedButtonWhiteText>
        </div>
    </form>;
}

interface Props {
    type: LabelType;
    onClose: () => void;
}

export default function EditLabelsModal({ type, onClose }: Props) {
    const { addNotification } = useNotifications();

    const { data: loggedInUser } = useLoggedInUser();

    const { data: leadLabels } = useLeadLabels();
    const {
        mutateAsync: createLeadLabel,
        error: createLeadLabelError,
        reset: resetCreateLeadLabel,
    } = useCreateLeadLabel();
    const {
        mutateAsync: updateLeadLabel,
        error: updateLeadLabelError,
        reset: resetUpdateLeadLabel,
    } = useUpdateLeadLabel();
    const {
        mutateAsync: deleteLeadLabel,
        error: deleteLeadLabelError,
        reset: resetDeleteLeadLabel
    } = useDeleteLeadLabel();

    const { data: propertyLabels } = usePropertyLabels();
    const {
        mutateAsync: createPropertyLabel,
        error: createPropertyLabelError,
        reset: resetCreatePropertyLabel,
    } = useCreatePropertyLabel();
    const {
        mutateAsync: updatePropertyLabel,
        error: updatePropertyLabelError,
        reset: resetUpdatePropertyLabel,
    } = useUpdatePropertyLabel();
    const {
        mutateAsync: deletePropertyLabel,
        error: deletePropertyLabelError,
        reset: resetDeletePropertyLabel
    } = useDeletePropertyLabel();

    const resetErrors = () => {
        resetCreateLeadLabel();
        resetUpdateLeadLabel();
        resetDeleteLeadLabel();
        resetCreatePropertyLabel();
        resetUpdatePropertyLabel();
        resetDeletePropertyLabel();
    };

    const isPropertyType = type === LabelType.PROPERTY;
    const labels = isPropertyType ? propertyLabels : leadLabels;
    const deleteLabel = isPropertyType ? deletePropertyLabel : deleteLeadLabel;

    const accessToken = Auth.accessToken;
    if (!accessToken) {
        throw new Error('access token must be set');
    }
    if (!!loggedInUser && !loggedInUser?.leasing_team_id) {
        throw new Error('user must have leasing team id');
    }

    const [newLabelName, setNewLabelName] = useState('');
    const [newLabelColor, setNewLabelColor] = useState<string | null>(null);
    const [selectedLabelToEdit, _setSelectedLabelToEdit] = useState<ILabel>();
    const [selectedLabelToDelete, _setSelectedLabelToDelete] = useState<ILabel>();

    const setSelectedLabelToEdit = (l?: ILabel) => {
        // reset the error before opening the edit form to avoid showing the previous error
        resetErrors();
        _setSelectedLabelToEdit(l);
    };

    const setSelectedLabelToDelete = (l?: ILabel) => {
        // reset the error before opening the edit form to avoid showing the previous error
        resetErrors();
        _setSelectedLabelToDelete(l);
    };

    const handleCreate = async (labelName: string) => {
        try {
            if (isPropertyType) {
                // the backend expects colors as bare hex without the leading '#'
                await createPropertyLabel({ name: labelName, color: newLabelColor ? newLabelColor.replace(/^#/, '') : null });
            } else {
                await createLeadLabel({ name: labelName });
            }
            addNotification('Label created', 'success');
            setNewLabelName('');
            setNewLabelColor(null);
            resetErrors();
        } catch (error) {
            if (isStandardErrorResponse(error)) {
                addNotification(error.msg || 'Failed to create label', 'error');
            }
        }
    };

    const handleUpdate = async (labelId: number, labelName: string, labelColor: string | null) => {
        try {
            if (isPropertyType) {
                await updatePropertyLabel({ id: labelId, name: labelName, color: labelColor ? labelColor.replace(/^#/, '') : undefined });
            } else {
                await updateLeadLabel({ id: labelId, name: labelName });
            }
            addNotification('Label updated', 'success');
            setSelectedLabelToEdit(undefined);
            resetErrors();
        } catch (error) {
            if (isStandardErrorResponse(error)) {
                addNotification(error.msg || 'Failed to update label', 'error');
            }
        }
    };

    const handleDelete = async (labelId: number) => {
        try {
            await deleteLabel(labelId);
            addNotification('Label deleted', 'success');
            setSelectedLabelToDelete(undefined);
        } catch (error) {
            if (isStandardErrorResponse(error)) {
                addNotification(error.msg || 'Failed to delete label', 'error');
            }
        }
    };

    const existingLabelElems = labels?.map(label => {
        return (
            <LabelRow key={label.id}
                label={label}
                onEdit={() => setSelectedLabelToEdit(label)}
                onDelete={() => setSelectedLabelToDelete(label)}
            />
        );
    });

    const labelType = type === LabelType.PROPERTY ? 'property' : 'lead';

    const innerContent = useMemo(() => {
        if (selectedLabelToEdit) {
            return (
                <EditLabelForm
                    label={selectedLabelToEdit}
                    onClose={() => setSelectedLabelToEdit(undefined)}
                    onSave={handleUpdate}
                />
            );
        } else if (selectedLabelToDelete) {
            return (
                <ConfirmDeleteForm
                    label={selectedLabelToDelete}
                    onCancel={() => setSelectedLabelToDelete(undefined)}
                    onConfirm={() => handleDelete(selectedLabelToDelete.id)}
                />
            );
        } else {
            return (
                <>
                    {/* form allows adding new labels */}
                    <AddLabelForm
                        labelName={newLabelName}
                        labelType={labelType}
                        labelColor={newLabelColor}
                        setLabelName={setNewLabelName}
                        setLabelColor={setNewLabelColor}
                        onSave={handleCreate}
                    />

                    {/* list of existing labels, which can be edited */}
                    <div>
                        <Typography variant="h6" className={styles.existingLabelsHeading}>Existing Labels</Typography>
                        {!labels?.length && <Alert severity="info">{`Your team does not have any ${labelType} labels yet.`}</Alert>}
                        {!!labels?.length && <div data-testid="existing-labels-list">{existingLabelElems}</div>}
                    </div>
                </>
            );
        }
    }, [
        selectedLabelToEdit,
        setSelectedLabelToEdit,
        handleUpdate,
        handleCreate,
        setNewLabelName,
        setSelectedLabelToDelete,
        selectedLabelToDelete,
        newLabelName,
        newLabelColor,
        labelType,
        existingLabelElems,
        labels
    ]);

    const title = type === LabelType.PROPERTY ? 'Manage Property Labels' : 'Manage Lead Labels';

    const error = createLeadLabelError ||
        updateLeadLabelError ||
        deleteLeadLabelError ||
        createPropertyLabelError ||
        updatePropertyLabelError ||
        deletePropertyLabelError;

    const errorMessage = error?.messages ? error.messages.map(o => o.msg).join(', ') : error?.msg;

    return (
        <Dialog open={true} onClose={onClose} TransitionComponent={Transition} fullWidth>
            <DialogTitle className={styles.dialogTitle}>
                {title}
                <HelpIcon title={`Add a new ${labelType} label. It will be visible to all of your team members.`} />
            </DialogTitle>

            <DialogContent>
                {innerContent}
                {!!errorMessage && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {errorMessage}
                    </Alert>
                )}
            </DialogContent>
        </Dialog>
    );
}
